/** Session Market API — aligned to Session-API Swagger (session-api.rimora.org/docs). */
import axios from 'axios'

const get_ip_url = 'https://api.ipify.org?format=json'
const adminUrl = import.meta.env.VITE_CUSTOM_SESSION as string
const adminUrlResult = (import.meta.env.VITE_CUSTOM_SESSION_RESULT as string) || adminUrl
const configUrl = import.meta.env.VITE_CONFIG_URL as string
export const commonSocketUrl = import.meta.env.VITE_COMMON_SOCKET_URL as string
const sportsbookUrl = import.meta.env.VITE_SPORTS_BOOK as string

export type AccessLevel = 'admin' | 'user' | string

let onTokenDeleted: (() => void) | null = null
export function setTokenDeletedHandler(fn: () => void) {
  onTokenDeleted = fn
}

function token(): string | null {
  return localStorage.getItem('accessToken')
}
function clientIp(): string | null {
  return localStorage.getItem('clientIP')
}

function checkTokenDeleted(data: unknown): boolean {
  if (data === 'Token deleted successfully') {
    onTokenDeleted?.()
    return true
  }
  if (data && typeof data === 'object' && 'data' in data && (data as { data: unknown }).data === 'Token deleted successfully') {
    onTokenDeleted?.()
    return true
  }
  return false
}

function authHeaders() {
  const t = token()
  return t ? { Authorization: `Bearer ${t}` } : {}
}

function num(v: unknown): number | undefined {
  if (v == null || v === '') return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : undefined
}

type AnyObj = Record<string, unknown>

function rateCategoryIdOf(row: AnyObj): string {
  const v = row.rateCategoryId
  if (v && typeof v === 'object') return String((v as AnyObj)._id ?? '')
  return String(v ?? '')
}

async function apiPost(path: string, body: unknown = {}, base = adminUrl) {
  const res = await axios.post(base + path, body ?? {}, { headers: authHeaders() })
  if (checkTokenDeleted(res.data)) throw new Error('Token deleted successfully')
  return res
}

export const sessionApi = {
  customUrl: adminUrl,
  commonSocketUrl,
  sportsbookUrl,

  getMaintenanceStatus: async (_data?: unknown) => {
    return axios.get(configUrl + '/api/maintenance/getMaintenanceStatus', {})
  },

  getGameConfigData: async (_data?: unknown) => {
    return axios.post(configUrl + '/api/GameConfig/search', {})
  },

  getIp: async () => axios.get(get_ip_url, {}),

  /** Lab Fetch Market — catalog sports / competitions / events. */
  getAllEventType: async () => apiPost('/v1/markets/get-all-event-types', {}),

  getAllCompetition: async (eventTypeId: string) =>
    apiPost('/v1/markets/get-all-competitions', { eventTypeId: String(eventTypeId ?? '') }),

  getAllMarketsForCompetition: async (betfairId: string) =>
    apiPost('/v1/markets/get-all-markets-for-competition', {
      betfairId: String(betfairId ?? ''),
    }),

  /** Empty body — live + upcoming cricket events from market.events */
  getAllMarkets: async () => apiPost('/v1/markets/get-markets', {}),

  /** SaveSubMarketDto */
  saveSubMarket: async (data: AnyObj) => {
    const body: AnyObj = {
      name: String(data.subMarketName ?? data.name ?? ''),
      markets: String((data.selectMarket as AnyObj)?._id ?? data.markets ?? ''),
    }
    const minBet = num(data.minBet)
    const maxBet = num(data.maxBet)
    const horseLay = num(data.horseLay ?? data.runner1)
    const horseBack = num(data.horseBack ?? data.runner2)
    const rateLay = num(data.rateLay)
    const rateBack = num(data.rateBack)
    if (minBet != null) body.minBet = minBet
    if (maxBet != null) body.maxBet = maxBet
    if (horseLay != null) body.horseLay = horseLay
    if (horseBack != null) body.horseBack = horseBack
    if (rateLay != null) body.rateLay = rateLay
    if (rateBack != null) body.rateBack = rateBack
    if (data.maxLimitsType != null) body.maxLimitsType = String(data.maxLimitsType)
    const suspendMaxLimit = num(data.suspendMaxLimit)
    if (suspendMaxLimit != null) body.suspendMaxLimit = suspendMaxLimit
    if (data.notification != null && String(data.notification) !== '') {
      body.notification = String(data.notification)
    }
    if (clientIp()) body.clientIp = clientIp()
    if (typeof data.isLambi === 'boolean') body.isLambi = data.isLambi
    return apiPost('/v1/sub-markets/save', body)
  },

  /** UpdateSubMarketDto */
  subMarketUpdate: async (id: string, data: AnyObj) => {
    const body: AnyObj = { marketId: id }
    if (typeof data.isSubMarketSuspended === 'boolean') body.isSuspended = data.isSubMarketSuspended
    if (typeof data.isSuspended === 'boolean') body.isSuspended = data.isSuspended
    if (data.runner1 != null && data.runner1 !== '') body.horseLay = num(data.runner1) ?? data.runner1
    if (data.runner2 != null && data.runner2 !== '') body.horseBack = num(data.runner2) ?? data.runner2
    if (data.horseLay != null) body.horseLay = num(data.horseLay) ?? data.horseLay
    if (data.horseBack != null) body.horseBack = num(data.horseBack) ?? data.horseBack
    if (data.rateBack != null) body.rateBack = num(data.rateBack)
    if (data.rateLay != null) body.rateLay = num(data.rateLay)
    if (data.name != null) body.name = String(data.name)
    if (typeof data.ballChalu === 'boolean') body.ballChalu = data.ballChalu
    if (data.runButtonValue != null && data.runButtonValue !== '') {
      const n = Number(data.runButtonValue)
      if (Number.isFinite(n)) body.runButtonValue = n
    }
    if (clientIp()) body.clientIp = clientIp()
    return apiPost('/v1/sub-markets/update', body)
  },

  /** VoidResultDto */
  subMarketUpdateForVoid: async (id: string, _data?: AnyObj) =>
    apiPost(
      '/v1/sub-markets/add-result-for-void',
      { marketId: id, ...(clientIp() ? { clientIp: clientIp() } : {}) },
      adminUrlResult,
    ),

  /** UpdateStatusDto */
  subMarketStatus: async (id: string, data: AnyObj) =>
    apiPost('/v1/sub-markets/update-status', {
      marketId: id,
      status: data.status,
    }),

  /** UpdateCommissionDto */
  updateCommission: async (subMarketId: string, data: AnyObj) =>
    apiPost('/v1/sub-markets/update-commission', {
      marketId: subMarketId,
      fancyCommission: Boolean(data.fancyCommission),
    }),

  /** UpdateBallChaluDto */
  updateBallChalu: async (id: string, data: AnyObj) =>
    apiPost('/v1/sub-markets/update-ball-chalu', {
      marketId: id,
      ballChalu: Boolean(data.ballChalu),
    }),

  /** MarketRefDto */
  getAllSubMarket: async (data: unknown, _index?: number) =>
    apiPost('/v1/sub-markets/get-all-for-backend', { market: String(data ?? '') }),

  /** Settled fancies — Lab-shaped { submarketList, runList, rateList }. */
  getAllResultDeclared: async (data: unknown) =>
    apiPost('/v1/sub-markets/get-all-result-declared', { market: String(data ?? '') }),

  /** Lab SubMarketInfo → Session-API. */
  getAllSubMarketInfo: async () => apiPost('/v1/sub-market-info/get-all', {}),

  getAllRates: async () => apiPost('/v1/rates/get-all', {}),

  /** AddResultDto */
  resultDeclared: async (data: AnyObj) =>
    apiPost(
      '/v1/sub-markets/add-result',
      {
        subMarketId: String(data.subMarketId ?? data.marketId ?? ''),
        horse: data.horse,
        ...(clientIp() ? { clientIp: clientIp() } : {}),
        ...(typeof data.requestFromParker === 'boolean'
          ? { requestFromParker: data.requestFromParker }
          : {}),
      },
      adminUrlResult,
    ),

  /** UpdateNameDto */
  editSessionName: async (data: AnyObj) =>
    apiPost('/v1/sub-markets/update-name', {
      marketId: String(data.marketId ?? ''),
      name: String(data.name ?? ''),
    }),

  /** Lab Result/reDeclareResult → Session-API. */
  reDeclareResult: async (data: AnyObj) =>
    apiPost('/v1/sub-markets/re-declare-result', {
      horse: data.horse,
      subMarketId: String(data.subMarketId ?? data.marketId ?? data._id ?? ''),
      marketId: data.marketId != null ? String(data.marketId) : undefined,
      _id: data._id != null ? String(data._id) : undefined,
    }),

  getAllRuns: async () => apiPost('/v1/rates/get-runs', {}),

  /** UpdateSuspendOrBallDto */
  marketSuspended: async (formId: string, market: AnyObj) => {
    const body: AnyObj = { marketId: formId }
    if (typeof market.isMarketSuspended === 'boolean') body.isMarketSuspended = market.isMarketSuspended
    if (typeof market.ballChalu === 'boolean') body.ballChalu = market.ballChalu
    if (clientIp()) body.clientIp = clientIp()
    return apiPost('/v1/sub-markets/update-suspend-or-ball-running', body)
  },

  /** RateSaveDto (runs) */
  saveRun: async (data: AnyObj) => apiPost('/v1/rates/save-run', { runs: num(data.runs) ?? 0 }),

  /** RateSaveDto */
  saveRate: async (data: AnyObj) => {
    const body: AnyObj = {}
    if (data.rateCategoryId != null) body.rateCategoryId = String(data.rateCategoryId)
    const rateLay = num(data.rateLay)
    const rateBack = num(data.rateBack)
    if (rateLay != null) body.rateLay = rateLay
    if (rateBack != null) body.rateBack = rateBack
    if (data.char != null) body.char = String(data.char)
    return apiPost('/v1/rates/save', body)
  },

  /** IdBodyDto */
  deleteOneRun: async (data: string) => apiPost('/v1/rates/delete-run', { id: data }),

  /** IdBodyDto */
  deleteOneRate: async (data: AnyObj) =>
    apiPost('/v1/rates/delete', { id: String(data.id ?? data._id ?? '') }),

  /** LoginDto */
  adminLogin: async (data: AnyObj) => {
    const body: AnyObj = {
      email: String(data.email ?? ''),
      password: String(data.password ?? ''),
    }
    if (data.clientIP != null) body.clientIP = String(data.clientIP)
    if (data.ip != null) body.ip = String(data.ip)
    if (data.device != null) body.device = String(data.device)
    return axios.post(adminUrl + '/v1/auth/login', body)
  },

  /** Empty body */
  removeToken: async () => apiPost('/v1/auth/logout', {}),

  /** Empty body */
  authorizedUser: async () => {
    try {
      return await apiPost('/v1/auth/authorized', {})
    } catch (err) {
      onTokenDeleted?.()
      throw err
    }
  },

  /** Empty body */
  getCurrentUser: async () => apiPost('/v1/auth/me', {}),

  getUserByAccessToken: async (_data?: AnyObj) => apiPost('/v1/auth/me', {}),

  /** IdBodyDto */
  getOneSubMarket: async (data: { subMarketId: string }) =>
    apiPost('/v1/sub-markets/get-one', { id: data.subMarketId }),

  /** Unused on new console — kept for type compat; use getAllResultDeclared. */
  getAllResult: async () => apiPost('/v1/sub-markets/get-all-result-declared', { market: '' }),

  /** UpdateSkyParkerDto */
  skySessioneventid: async (data: AnyObj) =>
    apiPost('/v1/markets/update-sky-session-event-id', {
      marketId: String(data.marketId ?? ''),
      ...(data.skySessioneventid != null ? { skySessioneventid: String(data.skySessioneventid) } : {}),
      ...(typeof data.skySession === 'boolean' ? { skySession: data.skySession } : {}),
      ...(typeof data.parkerSession === 'boolean' ? { parkerSession: data.parkerSession } : {}),
    }),

  /** MarketRefDto */
  getOneMarket: async (data: AnyObj) =>
    apiPost('/v1/markets/get-one', {
      ...(data.markets != null ? { markets: String(data.markets) } : {}),
      ...(data.market != null ? { market: String(data.market) } : {}),
      ...(data.marketId != null ? { marketId: String(data.marketId) } : {}),
      ...(data.id != null ? { id: String(data.id) } : {}),
      ...(data.betfairId != null ? { betfairId: String(data.betfairId) } : {}),
    }),

  /** UpdateOrderDto */
  changeOrder: async (id: string, order: unknown) =>
    apiPost('/v1/sub-markets/update-order', { marketId: id, order: num(order) ?? 0 }),

  /** UpdateBetDelayDto */
  changeBetDelay: async (id: string, betDelay: unknown) =>
    apiPost('/v1/sub-markets/update-bet-delay', { marketId: id, betDelay: num(betDelay) ?? 0 }),

  /** ChangeMarketBetDelayDto */
  changeMarketBetDelay: async (marketData: unknown, betDelay: unknown) => {
    const m = (marketData && typeof marketData === 'object' ? marketData : {}) as AnyObj
    return apiPost('/v1/markets/change-bet-delay', {
      market: { _id: String(m._id ?? '') },
      betDelay: num(betDelay) ?? 0,
    })
  },

  /** UpdateMinBetDto */
  changeMinBet: async (id: string, minBet: unknown) =>
    apiPost('/v1/sub-markets/update-min-bet', { marketId: id, minBet: num(minBet) ?? 0 }),

  /** UpdateMaxBetDto */
  changeMaxBet: async (id: string, maxBet: unknown) =>
    apiPost('/v1/sub-markets/update-max-bet', { marketId: id, maxBet: num(maxBet) ?? 0 }),

  /** UpdateSuspendMaxDto */
  changeSuspendMaxLimit: async (id: string, suspendMaxLimit: unknown) =>
    apiPost('/v1/sub-markets/update-suspend-max-limit', {
      marketId: id,
      suspendMaxLimit: num(suspendMaxLimit) ?? 0,
    }),

  /** UpdateNotificationDto */
  changeMarketNotification: async (id: string, notification: unknown) =>
    apiPost('/v1/sub-markets/update-notification', {
      marketId: id,
      notification: String(notification ?? ''),
    }),

  /** UpdateMaxMarketFancyDto */
  changeMaxMarketAmount: async (id: string, maxMarketAmountFancy: unknown) =>
    apiPost('/v1/sub-markets/update-max-market-amount-fancy', {
      marketId: id,
      maxMarketAmountFancy: num(maxMarketAmountFancy) ?? 0,
    }),

  /** Lab User CRUD → Session-API. */
  getAllUsers: async () => apiPost('/v1/users/get-all', {}),

  saveUserData: async (data: AnyObj) => {
    const payload = { ...data }
    delete payload._id
    return apiPost('/v1/users/create', payload)
  },

  getOneUser: async (data: AnyObj) =>
    apiPost('/v1/users/get-one', {
      userId: String(data.userId ?? data._id ?? data.id ?? ''),
    }),

  updateUser: async (data: AnyObj) => apiPost('/v1/users/update', data),

  deleteOneUser: async (data: string) =>
    apiPost('/v1/users/delete', { _id: data }),

  deleteOneUserOld: async (data: string) =>
    apiPost('/v1/users/delete', { _id: data }),

  /** Lab SubMarketInfo → Session-API. */
  saveSubmarketInfo: async (data: AnyObj) => apiPost('/v1/sub-market-info/save', data),

  getOneSubmarketInfo: async (data: AnyObj) =>
    apiPost('/v1/sub-market-info/get-one', {
      subMarketInfoId: String(data.subMarketInfoId ?? data._id ?? data.id ?? ''),
    }),

  updateSubMarketInfo: async (data: AnyObj) =>
    apiPost('/v1/sub-market-info/update', data),

  deleteOneSubMarketInfo: async (data: string) =>
    apiPost('/v1/sub-market-info/delete', { _id: data }),

  /** ChangeMaxLimitsTypeDto */
  changeMaxLimitsType: async (
    _subMarketId: string,
    maxLimitsType: unknown,
    _markets: unknown,
    marketId: unknown,
  ) =>
    apiPost('/v1/sub-markets/change-max-limits-type', {
      marketId: String(marketId ?? _subMarketId ?? ''),
      maxLimitsType: String(maxLimitsType ?? ''),
    }),

  /** ChangePasswordDto */
  changePassword: async (data: AnyObj) =>
    apiPost('/v1/auth/change-password', {
      password: String(data.password ?? ''),
      newPassword: String(data.newPassword ?? ''),
    }),

  /** RateSaveDto (name) */
  saveRateCategory: async (data: AnyObj) =>
    apiPost('/v1/rates/save-category', { name: String(data.name ?? '') }),

  getAllRateCategory: async () => apiPost('/v1/rates/get-categories', {}),

  /** PL event list — Session-API sportsbook passthrough (fallback: get-all-events). */
  getAllEvents: async () => {
    const res = await apiPost('/v1/markets/get-all-events-for-cs', {})
    if (Array.isArray(res.data) && res.data.length > 0) return res
    return apiPost('/v1/markets/get-all-events', {})
  },

  getAllEventsOld: async () => apiPost('/v1/markets/get-all-events', {}),

  /** Results page event dropdown. */
  getAllMarketsForResults: async () => apiPost('/v1/markets/get-all-events', {}),

  /** Client filter — no swagger filter endpoint */
  getFilterRates: async (data: AnyObj) => {
    const res = await apiPost('/v1/rates/get-all', {})
    const all = Array.isArray(res.data) ? (res.data as AnyObj[]) : []
    const catId = String(data._id ?? data.id ?? '')
    const filtered = all.filter((r) => rateCategoryIdOf(r) === catId)
    return { ...res, data: { data: filtered } }
  },

  filterRatesAndCatgData: async (data: AnyObj) => {
    const res = await apiPost('/v1/rates/get-all', {})
    const all = Array.isArray(res.data) ? (res.data as AnyObj[]) : []
    const catId = String(data._id ?? data.id ?? '')
    const filtered = all.filter((r) => rateCategoryIdOf(r) === catId)
    return { ...res, data: { data: filtered } }
  },

  /** Session-API get-pl-details — selectedUser is string id (all|sky|parker|userId). */
  filterEventsForDropdown: async (data: AnyObj) => {
    const event = (data.event && typeof data.event === 'object' ? data.event : data) as AnyObj
    const selected = data.selectedUser
    const selectedUser =
      typeof selected === 'string'
        ? selected
        : String((selected as AnyObj | undefined)?._id ?? 'all')
    return apiPost('/v1/markets/get-pl-details', {
      event: { _id: String(event._id ?? '') },
      selectedUser,
    })
  },

  /** Session-API CSV download (replaces old Sportsbook Excel). */
  downloadPLdetails: async (data: AnyObj) => {
    const event = (data.event && typeof data.event === 'object' ? data.event : data) as AnyObj
    const selected = data.selectedUser
    const selectedUser =
      typeof selected === 'string'
        ? selected
        : String((selected as AnyObj | undefined)?._id ?? 'all')
    const res = await apiPost('/v1/markets/download-pl-details', {
      event: {
        _id: String(event._id ?? ''),
        ...(event.betfairId != null ? { betfairId: String(event.betfairId) } : {}),
        ...(event.name != null ? { name: String(event.name) } : {}),
        ...(event.combineName != null ? { combineName: String(event.combineName) } : {}),
      },
      selectedUser,
    })
    const body = res.data as { csv?: string; filename?: string }
    if (body?.csv) {
      const blob = new Blob([body.csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = body.filename || 'pl-details.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    }
    return res
  },
}

export type SessionApi = typeof sessionApi
export default sessionApi
